import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import { Input, Popover, Icon } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ManufacturersDataItem } from '../../api/productAssociation.api';
import styles from './LookupOptions.module.less';

const { Search } = Input;

type ManufacturersProps = {
  data: ManufacturersDataItem[];
  currentValue: ManufacturersDataItem[];
  onChange: (val: ManufacturersDataItem) => void;
  currentSourceSite?: string | null;
  setManufacturerName: (value: string) => void;
  loading: boolean;
};

const popoverContent = (
  <div>
    <p>
      Example: ABC* would return all results that begin with the letters ABC, but contain more alphanumeric characters
      after.
    </p>
    <p>
      Example: *ABC would return all results that end with the letters ABC, but contain more alphanumeric characters
      before.
    </p>
    <p>
      Example: AB*C would return all results that start with the letters AB and end with the letter C, but contain more
      alphanumeric characters in between.
    </p>
  </div>
);

const minLength = 3;

const getCleanedValue = (value: string) => {
  return value.trim().replace(/[*]/g, '');
};

export const Manufacturers: React.FC<ManufacturersProps> = ({
  data,
  onChange,
  setManufacturerName,
  currentValue,
  loading,
}) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [isFocusSearch, setIsFocusSearch] = useState<boolean>(false);
  const cleanedSearchValue = getCleanedValue(searchValue);
  const isValueTooShort = cleanedSearchValue.length < minLength;
  const showWarning = isFocusSearch && isValueTooShort;
  const manufacturerIds = currentValue.map(({ id }) => id);
  const setManufacturerValue = useMemo(() => debounce(setManufacturerName, 300), [setManufacturerName]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    const cleanedValue = getCleanedValue(value);
    if (cleanedValue.length >= minLength) {
      setManufacturerValue(value.trim());
    }
  };

  return (
    <div className={styles.blockManufacturer}>
      <div className={styles.wrapBlockSearch}>
        <div className={styles.xRowSpaceBetween}>
          <Search
            className={clsx(styles.searchManufacturers, isValueTooShort && styles.searchManufacturersWarning)}
            placeholder="Enter a Manufacturer Name"
            value={searchValue}
            onSearch={handleSearch}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => setIsFocusSearch(true)}
            onBlur={() => setIsFocusSearch(false)}
            loading={loading}
            allowClear={true}
          />
          <Popover content={popoverContent}>
            <FontAwesomeIcon icon={['fal', 'question-circle']} size={'lg'} />
          </Popover>
        </div>
        {showWarning && (
          <p className={styles.textWarning}>
            Manufacturer lookups require a minimum of {minLength} alphanumeric characters prior to results returning.
          </p>
        )}
      </div>

      {data.length > 0 && searchValue ? (
        <div className={styles.expandList}>
          {data.map((item) => (
            <p
              className={clsx(styles.dropdownItem, manufacturerIds.includes(item.id) && styles.active)}
              onClick={() => onChange(item)}
              key={item.id}
            >
              {item.name}
              <Icon type="check" />
            </p>
          ))}
        </div>
      ) : (
        !isValueTooShort && !loading && <p>No result found</p>
      )}
      <div className={styles.blockSelected}>
        {currentValue.map((item) => {
          return (
            <div className={styles.itemBlockSelected} key={item.id}>
              <p className={styles.textItemBlockSelected}>{item.name}</p>
              <FontAwesomeIcon
                icon={['fas', 'times-circle']}
                className={styles.iconRemove}
                onClick={() => onChange(item)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
